import { Box } from '@mantine/core';
import type { JSX } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, IOnboardingMilestone } from '@feathr/blackbox';
import { CampaignState, EOnboardingMilestone, EOnboardingTaskState } from '@feathr/blackbox';
import { Breadcrumbs, Button, Layout, showError, Well } from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import MascotAssistant from '@feathr/extender/components/MascotAssistant';
import { useAccount, useLocalUrl, useStore } from '@feathr/extender/state';
import type { ListResponse } from '@feathr/rachis';

import Milestones from './Milestones';
import type { IMilestoneHint } from './Milestones/Milestones';

import mascotImage from '@feathr/extender/images/super_pixel_mascot.png';

// Check if all milestones are completed
export const allMilestones = [
  EOnboardingMilestone.InitialAccountSetup,
  EOnboardingMilestone.SingleSendCampaign,
  EOnboardingMilestone.OmniCampaign,
];

// Find the most recently completed milestone based on priority order
export const priorityOrder = [
  EOnboardingMilestone.OmniCampaign,
  EOnboardingMilestone.SingleSendCampaign,
  EOnboardingMilestone.InitialAccountSetup,
];

export function filterByPublishedCampaigns(campaigns: ListResponse<Campaign>): Campaign[] {
  return campaigns.models.filter(
    (campaign: Campaign) => campaign.get('state') === CampaignState.Published,
  );
}

function OnboardingPage(): JSX.Element {
  const { t } = useTranslation();

  const { OnboardingTasks, Campaigns } = useStore();
  const account = useAccount();
  const localUrl = useLocalUrl();
  const [milestones, setMilestones] = useState<IOnboardingMilestone[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hints, setHints] = useState<IMilestoneHint[]>([]);

  useEffect(() => {
    const fetchMilestones = async (): Promise<void> => {
      try {
        const response = await OnboardingTasks.getMilestones();
        setMilestones(response);
      } catch (error) {
        showError(error, t);
      } finally {
        setIsLoading(false);
      }
    };
    fetchMilestones();
  }, []);

  // Check for published campaigns vs account logo and create hints
  useEffect(() => {
    const createHints = async (): Promise<void> => {
      try {
        const newHints: IMilestoneHint[] = [];

        // Use count aggregation to check for published campaigns
        const publishedCampaignCount = Campaigns.count({
          filters: { state__eq: CampaignState.Published },
        });
        const hasPublishedCampaign = publishedCampaignCount > 0;
        const hasAccountLogo = !!account.get('logo');

        // Add hint for published campaigns without logo
        if (hasPublishedCampaign && !hasAccountLogo) {
          newHints.push({
            milestone: EOnboardingMilestone.OmniCampaign,
            content: (
              <Box mt={'md'}>
                <Well theme={'blue'}>
                  <div>
                    {t(
                      "It looks like you've already published a campaign but have not uploaded a logo.",
                    )}
                  </div>
                  <Button link={localUrl('/settings/account/profile')} type={'primary'}>
                    {t('Upload your logo')}
                  </Button>
                </Well>
              </Box>
            ),
          });
        }

        setHints(newHints);
      } catch (error) {
        // Silently handle error - this is non-critical functionality
        setHints([]);
      }
    };

    if (!isLoading) {
      createHints();
    }
  }, [isLoading, Campaigns, account, t]);

  const getMascotMessage = (milestones: IOnboardingMilestone[]): string => {
    const milestoneMessages = {
      [EOnboardingMilestone.InitialAccountSetup]: t(
        'You’re done getting started. Now it’s time to publish your first campaign. Retargeting or email... your choice.',
      ),
      [EOnboardingMilestone.SingleSendCampaign]: t(
        'You’re almost there! Just publish an ad campaign and you’ll be done.',
      ),
      [EOnboardingMilestone.OmniCampaign]: t(
        'You’re almost there! Just publish an email campaign and you’ll be done.',
      ),
    };

    const allCompletedMessage = t(
      'Congratulations on completing your Feathr onboarding! For additional assistance contact our Support team.',
    );
    const defaultMessage = t("Hi there! I'm Pixl. I'm here to help you set your account up.");

    if (milestones.length === 0) {
      return defaultMessage;
    }

    const allCompleted = allMilestones.every((milestoneType) => {
      const milestone = milestones.find((m) => m.milestone === milestoneType);
      return milestone?.state === EOnboardingTaskState.Completed;
    });

    if (allCompleted) {
      return allCompletedMessage;
    }

    for (const milestoneType of priorityOrder) {
      const milestone = milestones.find((m) => m.milestone === milestoneType);
      if (milestone?.state === EOnboardingTaskState.Completed) {
        return milestoneMessages[milestoneType];
      }
    }

    return defaultMessage;
  };

  const mascotMessage = useMemo(() => getMascotMessage(milestones), [milestones]);

  const breadcrumbs = (
    <Breadcrumbs
      breadcrumbItems={[
        {
          key: 'quickstart',
          label: t('Quick Start'),
          path: '/quickstart',
          depth: 0,
        },
      ]}
      includeCurrentPage={true}
    />
  );

  return (
    <Page
      BreadcrumbsComponent={breadcrumbs}
      collapseHeader={false}
      description={t('Complete these milestones to get started with your account')}
      title={t('Welcome to Feathr')}
    >
      <Layout
        sideContent={<MascotAssistant mascotImage={mascotImage} text={mascotMessage} />}
        width={'full'}
      >
        <Milestones hints={hints} isLoading={isLoading} milestones={milestones} />
      </Layout>
    </Page>
  );
}

export default OnboardingPage;
